/**
 * LakehouseCatalog Page
 *
 * Main catalog view for managing all lakehouses with protection tiers and compliance tags.
 * Implements US2 - Lakehouse Catalog from Constitution v2.0.0.
 *
 * Requirements:
 * - FR-201: View all lakehouses with protection tier
 * - FR-202: Filter by tier, environment, compliance
 * - FR-203: Assign protection tier
 * - FR-204: Update compliance tags
 * - FR-205: Bulk tier assignment
 *
 * Conformed to Spec 197 design blueprint — presentation migrated onto the canonical
 * Tailwind+token UI kit (PageLayout / Card / DataTable / Modal / kit form controls);
 * no data, logic, state, route, or behavior changes.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Database, Eye, Search, FileText, RefreshCw, X, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import {
  useCatalogData,
  LakehouseCatalogFilters,
  CatalogPagination,
  LakehouseWithWorkspace,
  ProtectionTier,
  useBulkAssignTier,
} from '../hooks/useLakehouses';
import { LakehouseEditModal } from '../components/catalog/LakehouseEditModal';
import { ProtectionTierBadge, TierSelector } from '../components/common/ProtectionTierBadge';
import { ComplianceTagChips } from '../components/common/ComplianceTagChips';
import { LakehouseSizeCell } from '../components/common/LakehouseSizeCell';
import { unmeasuredSizeNote } from '../utils/entitySize';
import { lakehouseHelp } from '../../../data/help/lakehouseHelp';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Input,
  Select,
  Badge,
  StatCard,
  Checkbox,
  FilterBar,
  EmptyState,
  LoadingState,
  Modal,
  Extended,
  QuickGlance,
  AppTooltip,
  PageHelpPanel,
} from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import { formatEasternDate } from '@/utils/dateFormatter';

const DEFAULT_PAGE_SIZE = 20;

// ----------------------------------------------------------------------------
// Filter option sets (unchanged from the legacy filter component)
// ----------------------------------------------------------------------------

const PROTECTION_TIERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All Tiers' },
  { value: 'Gold', label: '🥇 Gold (4h RPO)' },
  { value: 'Silver', label: '🥈 Silver (12h RPO)' },
  { value: 'Bronze', label: '🥉 Bronze (24h RPO)' },
];

const ENVIRONMENTS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All Environments' },
  { value: 'prod', label: 'Production' },
  { value: 'non-prod', label: 'Non-Production' },
];

const COMPLIANCE_TAGS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All Tags' },
  { value: 'PII', label: 'PII - Personal Data' },
  { value: 'HIPAA', label: 'HIPAA - Healthcare' },
  { value: 'GDPR', label: 'GDPR - EU Privacy' },
  { value: 'SOX', label: 'SOX - Financial' },
  { value: 'PCI', label: 'PCI - Payment Card' },
  { value: 'Financial', label: 'Financial Data' },
  { value: 'Critical', label: 'Business Critical' },
];

const REVIEW_FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All Status' },
  { value: 'true', label: '⚠️ Review Required' },
  { value: 'false', label: '✓ Reviewed' },
];

// ----------------------------------------------------------------------------
// Cell formatting helpers (preserved from the legacy table component)
// ----------------------------------------------------------------------------

function formatSize(sizeGb?: number | null): string {
  if (!sizeGb) return '-';
  if (sizeGb < 1) return `${Math.round(sizeGb * 1024)} MB`;
  if (sizeGb >= 1024) return `${(sizeGb / 1024).toFixed(1)} TB`;
  return `${sizeGb.toFixed(1)} GB`;
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  const now = new Date();
  const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffHours < 48) return 'Yesterday';
  if (diffHours < 168) return `${Math.floor(diffHours / 24)}d ago`;

  return formatEasternDate(date);
}

/**
 * Spec 216 T029: the catalog renders standalone (legacy /lakehouses/catalog,
 * now redirected) OR embedded as the "Protection" tab of the Lakehouse List.
 */
const LakehouseCatalog: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  // Filter and pagination state
  const [filters, setFilters] = useState<LakehouseCatalogFilters>({});
  const [pagination, setPagination] = useState<CatalogPagination>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    sortBy: 'lakehouseName',
    sortOrder: 'asc',
  });

  // Selection state for bulk operations
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkTier, setBulkTier] = useState<string>('Bronze');

  // Edit modal state
  const [editingLakehouse, setEditingLakehouse] = useState<LakehouseWithWorkspace | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Fetch catalog data
  const { catalog, stats, isLoading, refetch } = useCatalogData(filters, pagination);
  const bulkAssignMutation = useBulkAssignTier();

  // Handlers
  const handleFiltersChange = useCallback((newFilters: LakehouseCatalogFilters) => {
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, page: 1 })); // Reset to first page on filter change
    setSelectedIds(new Set()); // Clear selection on filter change
  }, []);

  const handleFilterField = useCallback(
    (key: keyof LakehouseCatalogFilters, value: string) => {
      const newFilters = { ...filters };
      if (value === '') {
        delete newFilters[key];
      } else if (key === 'reviewRequired' || key === 'enabled') {
        newFilters[key] = value === 'true';
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (newFilters as any)[key] = value;
      }
      handleFiltersChange(newFilters);
    },
    [filters, handleFiltersChange],
  );

  const handleClearFilters = useCallback(() => {
    handleFiltersChange({});
  }, [handleFiltersChange]);

  const handleSort = useCallback((column: string) => {
    setPagination((prev) => ({
      ...prev,
      sortBy: column,
      sortOrder: prev.sortBy === column && prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleRowClick = useCallback((lakehouse: LakehouseWithWorkspace) => {
    setEditingLakehouse(lakehouse);
    setIsEditModalOpen(true);
  }, []);

  const handleEditModalClose = useCallback(() => {
    setIsEditModalOpen(false);
    setEditingLakehouse(null);
  }, []);

  const handleEditSaved = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleBulkAssign = useCallback(async () => {
    if (selectedIds.size === 0) return;

    try {
      await bulkAssignMutation.mutateAsync({
        ids: Array.from(selectedIds),
        tier: bulkTier as ProtectionTier,
      });
      setSelectedIds(new Set());
      setShowBulkModal(false);
      refetch();
    } catch (error) {
      console.error('Bulk assign failed:', error);
    }
  }, [selectedIds, bulkTier, bulkAssignMutation, refetch]);

  // Computed values
  const lakehouses = useMemo(() => catalog.data?.data || [], [catalog.data]);
  const paginationInfo = useMemo(() => catalog.data?.pagination, [catalog.data]);
  const statsData = useMemo(() => stats.data, [stats.data]);

  const totalPages = paginationInfo?.totalPages || 1;
  const currentPage = paginationInfo?.page || 1;
  const totalItems = paginationInfo?.total || 0;

  const hasActiveFilters = Object.keys(filters).length > 0;
  const allSelected = selectedIds.size === lakehouses.length && lakehouses.length > 0;
  const someSelected = selectedIds.size > 0 && selectedIds.size < lakehouses.length;

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === lakehouses.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(lakehouses.map((l) => l.id)));
    }
  }, [lakehouses, selectedIds]);

  const toggleSelectRow = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Header sort-affordance icon.
  const SortIcon: React.FC<{ column: string }> = ({ column }) => {
    if (pagination.sortBy !== column) {
      return <ChevronsUpDown className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" />;
    }
    return pagination.sortOrder === 'asc' ? (
      <ChevronUp className="h-3.5 w-3.5 text-[var(--color-accent)]" />
    ) : (
      <ChevronDown className="h-3.5 w-3.5 text-[var(--color-accent)]" />
    );
  };

  const sortHeader = (label: string, column: string) => (
    <button
      type="button"
      onClick={() => handleSort(column)}
      className="inline-flex items-center gap-1 text-[var(--color-text-primary)] hover:text-[var(--color-accent)]"
    >
      {label}
      <SortIcon column={column} />
    </button>
  );

  const columns: DataTableColumn<LakehouseWithWorkspace>[] = [
    {
      key: 'select',
      header: (
        <Checkbox
          aria-label="Select all"
          checked={allSelected}
          ref={(el) => {
            if (el) el.indeterminate = someSelected;
          }}
          onChange={toggleSelectAll}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      width: '4%',
      sortable: false,
      render: (l) => (
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox
            aria-label={`Select ${l.lakehouseName}`}
            checked={selectedIds.has(l.id)}
            onChange={() => toggleSelectRow(l.id)}
          />
        </span>
      ),
    },
    {
      key: 'lakehouseName',
      header: sortHeader('Lakehouse', 'lakehouseName'),
      width: '22%',
      render: (l) => (
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 flex-shrink-0 text-[var(--color-accent)]" />
            <span className="font-medium text-[var(--color-text-primary)]">{l.lakehouseName}</span>
            {l.reviewRequired && (
              <AppTooltip title="Review required - auto-classified tier">
                <Badge variant="warning" size="xs">
                  ⚠️
                </Badge>
              </AppTooltip>
            )}
            {!l.enabled && (
              <Badge variant="secondary" size="xs">
                Disabled
              </Badge>
            )}
          </div>
          {l.description && (
            <p className="mt-0.5 truncate text-xs text-[var(--color-text-secondary)]">{l.description}</p>
          )}
        </div>
      ),
    },
    {
      key: 'workspaceDisplayName',
      header: sortHeader('Workspace', 'workspaceDisplayName'),
      width: '16%',
      render: (l) => <span className="text-[var(--color-text-secondary)]">{l.workspaceDisplayName}</span>,
    },
    {
      key: 'protectionTier',
      header: sortHeader('Protection Tier', 'protectionTier'),
      width: '13%',
      render: (l) => (
        <ProtectionTierBadge tier={l.protectionTier} source={l.protectionTierSource} size="sm" />
      ),
    },
    {
      key: 'environment',
      header: sortHeader('Environment', 'environment'),
      width: '11%',
      render: (l) => (
        <Badge variant={l.environment === 'prod' ? 'success' : 'secondary'} size="sm">
          {l.environment === 'prod' ? 'Prod' : 'Non-Prod'}
        </Badge>
      ),
    },
    {
      key: 'complianceTags',
      header: 'Compliance',
      width: '15%',
      render: (l) => <ComplianceTagChips tags={l.complianceTags} maxDisplay={2} size="sm" />,
    },
    {
      key: 'sizeGb',
      header: sortHeader('Size', 'sizeGb'),
      width: '8%',
      align: 'right',
      render: (l) => (
        <LakehouseSizeCell
          sizeBytes={l.sizeBytes}
          tableCount={l.tableCount}
          sizeGb={l.sizeGb}
          sizeError={l.sizeMeasurementError}
          lastRecordedGb={l.sizeLastRecordedGb}
          lastRecordedOn={l.sizeLastRecordedOn}
        />
      ),
    },
    {
      key: 'lastBackupAt',
      header: sortHeader('Last Backup', 'lastBackupAt'),
      width: '8%',
      render: (l) => <span className="text-[var(--color-text-secondary)]">{formatDate(l.lastBackupAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '3%',
      align: 'right',
      render: (l) => (
        <AppTooltip title="Edit lakehouse">
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(l);
            }}
          >
            <Eye className="h-4 w-4" />
          </Button>
        </AppTooltip>
      ),
    },
  ];

  const pageActions = (
    <>
      <PageHelpPanel content={lakehouseHelp} buttonSize="sm" />
      {selectedIds.size > 0 && (
        <AppTooltip title="Assign a protection tier to all selected lakehouses">
          <Button leftIcon={<FileText className="h-4 w-4" />} onClick={() => setShowBulkModal(true)}>
            Bulk Assign Tier ({selectedIds.size})
          </Button>
        </AppTooltip>
      )}
      <Button
        variant="outline"
        leftIcon={<RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />}
        onClick={() => refetch()}
        disabled={isLoading}
      >
        Refresh
      </Button>
    </>
  );

  // Shared body — stats, filters, table, and modals (used by both renders).
  const renderBody = () => (
    <>
      {/* Stats Summary */}
      {statsData && (
        <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-6">
          <StatCard label="Total Lakehouses" value={statsData.total} />
          <StatCard
            label="Total Backup Size"
            value={formatSize(statsData.totalSizeGb)}
            subtext={unmeasuredSizeNote(statsData.sizeNotMeasuredCount)}
            valueClassName="text-[var(--color-accent)]"
          />
          <AppTooltip title="Lakehouses assigned to Gold protection tier">
            <div>
              <StatCard
                label="Gold Tier"
                value={statsData.byTier?.Gold || 0}
                valueClassName="text-amber-600 dark:text-amber-400"
              />
            </div>
          </AppTooltip>
          <StatCard
            label="Silver Tier"
            value={statsData.byTier?.Silver || 0}
            valueClassName="text-[var(--color-text-secondary)]"
          />
          <StatCard
            label="Bronze Tier"
            value={statsData.byTier?.Bronze || 0}
            valueClassName="text-orange-600 dark:text-orange-400"
          />
          <AppTooltip title="Starter = default tier — assign a protection tier to set its recovery objective">
            <div>
              <StatCard
                label="Starter Tier"
                value={(statsData.byTier as Record<string, number> | undefined)?.Starter || 0}
                valueClassName={
                  ((statsData.byTier as Record<string, number> | undefined)?.Starter || 0) > 0
                    ? 'text-yellow-600 dark:text-yellow-400'
                    : undefined
                }
              />
            </div>
          </AppTooltip>
        </div>
      )}

      <QuickGlance>
        <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
          Showing the at-a-glance summary. Open this page on a larger screen for the full details.
        </Card>
      </QuickGlance>

      <Extended>
      {/* Filters — canonical FilterBar (kit Input/Select/Button controls) */}
      <FilterBar
        trailing={
          <span className="whitespace-nowrap text-sm text-[var(--color-text-secondary)]">
            {totalItems} {totalItems === 1 ? 'lakehouse' : 'lakehouses'}
          </span>
        }
      >
        <div className="w-56">
          <Input
            isFullWidth
            placeholder="Search lakehouses..."
            value={filters.search || ''}
            onChange={(e) => handleFilterField('search', e.target.value)}
            disabled={isLoading}
            leftAddon={<Search className="h-4 w-4" />}
          />
        </div>
        <Select
          label="Protection Tier"
          value={(filters.protectionTier as string) || ''}
          onChange={(e) => handleFilterField('protectionTier', e.target.value)}
          disabled={isLoading}
          options={PROTECTION_TIERS}
        />
        <Select
          label="Environment"
          value={(filters.environment as string) || ''}
          onChange={(e) => handleFilterField('environment', e.target.value)}
          disabled={isLoading}
          options={ENVIRONMENTS}
        />
        <Select
          label="Compliance"
          value={filters.complianceTag || ''}
          onChange={(e) => handleFilterField('complianceTag', e.target.value)}
          disabled={isLoading}
          options={COMPLIANCE_TAGS}
        />
        <Select
          label="Review Status"
          value={filters.reviewRequired === undefined ? '' : String(filters.reviewRequired)}
          onChange={(e) => handleFilterField('reviewRequired', e.target.value)}
          disabled={isLoading}
          options={REVIEW_FILTERS}
        />
      </FilterBar>

      {hasActiveFilters && (
        <div className="mb-6 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-[var(--color-text-secondary)]">Active:</span>
          {filters.protectionTier && (
            <FilterBadge label={`Tier: ${filters.protectionTier}`} onRemove={() => handleFilterField('protectionTier', '')} />
          )}
          {filters.environment && (
            <FilterBadge label={`Env: ${filters.environment}`} onRemove={() => handleFilterField('environment', '')} />
          )}
          {filters.complianceTag && (
            <FilterBadge label={`Tag: ${filters.complianceTag}`} onRemove={() => handleFilterField('complianceTag', '')} />
          )}
          {filters.reviewRequired !== undefined && (
            <FilterBadge
              label={filters.reviewRequired ? 'Review Required' : 'Reviewed'}
              onRemove={() => handleFilterField('reviewRequired', '')}
            />
          )}
          {filters.search && (
            <FilterBadge label={`Search: "${filters.search}"`} onRemove={() => handleFilterField('search', '')} />
          )}
          <Button size="sm" variant="ghost" onClick={handleClearFilters} disabled={isLoading}>
            Clear Filters
          </Button>
        </div>
      )}

      {/* Table */}
      <Card padding="none">
        {catalog.isLoading ? (
          <div className="p-4">
            <LoadingState rows={6} />
          </div>
        ) : lakehouses.length === 0 ? (
          <EmptyState
            icon={<Database className="h-10 w-10" />}
            title="No lakehouses found"
            message="Try adjusting your filters or add new lakehouses."
          />
        ) : (
          <DataTable
            columns={columns}
            rows={lakehouses}
            rowKey={(l) => l.id}
            pageSize={pagination.pageSize}
            showPagination={false}
            onRowClick={handleRowClick}
            emptyMessage="No lakehouses found"
            minWidth="64rem"
          />
        )}
      </Card>

      {/* Server-side pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-[var(--color-text-secondary)]">
            Showing {(currentPage - 1) * pagination.pageSize + 1} to{' '}
            {Math.min(currentPage * pagination.pageSize, totalItems)} of {totalItems} lakehouses
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || isLoading}
            >
              Previous
            </Button>
            <span className="px-2 text-sm text-[var(--color-text-secondary)]">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || isLoading}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      </Extended>

      {/* Edit Modal */}
      <LakehouseEditModal
        lakehouse={editingLakehouse}
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        onSaved={handleEditSaved}
      />

      {/* Bulk Assign Modal */}
      <Modal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        title="Bulk Assign Protection Tier"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowBulkModal(false)} disabled={bulkAssignMutation.isPending}>
              Cancel
            </Button>
            <Button onClick={handleBulkAssign} isLoading={bulkAssignMutation.isPending}>
              {bulkAssignMutation.isPending ? 'Assigning...' : 'Assign Tier'}
            </Button>
          </>
        }
      >
        <p className="mb-4 text-sm text-[var(--color-text-secondary)]">
          Assign a protection tier to <strong>{selectedIds.size}</strong> selected lakehouses.
        </p>
        <TierSelector value={bulkTier} onChange={setBulkTier} />
      </Modal>
    </>
  );

  // Embedded (Protection tab of the Lakehouse List): no PageLayout chrome.
  if (embedded) {
    return (
      <div>
        <div className="mb-4 flex flex-wrap items-center justify-end gap-2">{pageActions}</div>
        {renderBody()}
      </div>
    );
  }

  return (
    <PageLayout
      title="Lakehouse Catalog"
      description="Manage protection tiers and compliance tags for all lakehouses"
      icon={<Database className="h-6 w-6" />}
      actions={pageActions}
    >
      {renderBody()}
    </PageLayout>
  );
};

// ----------------------------------------------------------------------------
// Active-filter chip (token-styled; replaces the legacy hand-rolled badge)
// ----------------------------------------------------------------------------

interface FilterBadgeProps {
  label: string;
  onRemove: () => void;
}

const FilterBadge: React.FC<FilterBadgeProps> = ({ label, onRemove }) => (
  <Badge variant="primary" size="sm">
    {label}
    <button type="button" onClick={onRemove} className="ml-1 hover:opacity-70 focus:outline-none" aria-label="Remove filter">
      <X className="h-3 w-3" />
    </button>
  </Badge>
);

export default LakehouseCatalog;
